import { Fragment, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Loader2,
  CheckCircle2,
  ClipboardList,
  Phone,
  FileDown,
  Copy,
  Check,
  Ruler,
  Plus,
} from "lucide-react";
import { toast } from "sonner";
import { api, money, unitPrice, defaultSelection, variantText, quotePdfUrl, slug, effectiveOptions, toggleMulti, setMultiQty } from "@/lib/api";
import { Header } from "@/components/Header";
import { OptionCard } from "@/components/OptionCard";
import { SummaryPanel } from "@/components/SummaryPanel";
import { StandardFeatures } from "@/components/StandardFeatures";
import { BoatStep } from "@/components/BoatStep";
import { QuotePicker } from "@/components/QuotePicker";
import { hullOf, hullLabel, productFitsHull, hasFitRules } from "@/lib/fit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const HERO = "/hero.jpg";
const SESSION_KEY = "fdm_builder_session";

export default function Builder() {
  const [categories, setCategories] = useState([]);
  const [productsByCat, setProductsByCat] = useState({});
  const [selections, setSelections] = useState({}); // id -> {product, quantity, selectType, variants, addons}
  const [brands, setBrands] = useState([]);
  const [motorBrands, setMotorBrands] = useState([]);
  const [optionSets, setOptionSets] = useState({});
  const [view, setView] = useState("hero"); // hero | pick | build | review | done
  const [activeKeys, setActiveKeys] = useState([]); // category keys the customer is quoting
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ name: "", phone: "", email: "", comments: "" });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const [showAllFits, setShowAllFits] = useState({}); // category key -> bool
  const [loaded, setLoaded] = useState(false);

  const catLabels = useMemo(
    () => Object.fromEntries(categories.map((c) => [c.key, c.label])),
    [categories]
  );

  useEffect(() => {
    (async () => {
      try {
        const [cats, prods, br, mbr, os] = await Promise.all([
          api.get("/categories"),
          api.get("/products"),
          api.get("/boat-brands"),
          api.get("/brands/motor"),
          api.get("/option-sets"),
        ]);
        setCategories(cats.data);
        setBrands(br.data);
        setMotorBrands(mbr.data);
        const sets = Object.fromEntries(os.data.map((o) => [o.id, o]));
        setOptionSets(sets);
        const grouped = {};
        for (const p of prods.data) {
          (grouped[p.category] = grouped[p.category] || []).push(p);
        }
        setProductsByCat(grouped);

        const code = new URLSearchParams(window.location.search).get("build");
        if (code) await loadSharedBuild(code, cats.data, prods.data, sets);
        else restoreSession(prods.data);
        setLoaded(true);
      } catch (e) {
        toast.error("Could not load products. Please refresh.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const restoreSession = (prods) => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(SESSION_KEY) || "null");
      if (!saved || saved.view === "hero") return;
      const byId = Object.fromEntries(prods.map((p) => [p.id, p]));
      const next = {};
      for (const [id, l] of Object.entries(saved.selections || {})) {
        if (byId[id]) next[id] = { ...l, product: byId[id] };
      }
      setSelections(next);
      setActiveKeys(saved.activeKeys || []);
      setStep(saved.step || 0);
      setView(saved.view);
    } catch (e) {
      sessionStorage.removeItem(SESSION_KEY);
    }
  };

  useEffect(() => {
    if (!loaded) return;
    if (view === "done") return sessionStorage.removeItem(SESSION_KEY);
    const slim = Object.fromEntries(Object.entries(selections).map(([id, l]) => [id, { ...l, product: undefined }]));
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({ view, activeKeys, step, selections: slim }));
  }, [loaded, view, activeKeys, step, selections]);

  const loadSharedBuild = async (code, cats, prods, sets) => {
    try {
      const res = await api.get(`/builds/${code}`);
      const selectOf = Object.fromEntries(cats.map((c) => [c.key, c.select]));
      const byId = Object.fromEntries(prods.map((p) => [p.id, p]));
      const next = {};
      for (const it of res.data.items) {
        const product = byId[it.product_id];
        if (!product) continue;
        next[product.id] = {
          product,
          quantity: Math.max(1, it.quantity || 1),
          selectType: selectOf[product.category] || "multi",
          variants: { ...defaultSelection(product, sets).variants, ...(it.variants || {}) },
          addons: it.addons || [],
        };
      }
      setSelections(next);
      const keys = cats.map((c) => c.key).filter((k) => Object.values(next).some((l) => l.product.category === k));
      setActiveKeys(keys.length ? keys : cats.map((c) => c.key));
      setView("build");
      setStep(0);
      toast.success(`Shared build ${code} loaded — make it yours.`);
    } catch (e) {
      toast.error("That shared build link could not be found.");
    }
  };

  const lines = useMemo(
    () => Object.values(selections).sort((a, b) => a.product.category.localeCompare(b.product.category)),
    [selections]
  );
  const shippingTotal = useMemo(() => lines.reduce((s, l) => s + Number(l.product.shipping || 0) * l.quantity, 0), [lines]);
  const total = useMemo(
    () => lines.reduce((s, l) => s + unitPrice(l.product, l.variants, l.addons, optionSets) * l.quantity, 0) + shippingTotal,
    [lines, optionSets, shippingTotal]
  );
  const [boatBrand, setBoatBrand] = useState("");
  const boatLine = lines.find((l) => l.product.category === "boat");
  const motorLine = lines.find((l) => l.product.category === "motor");

  const steps = useMemo(() => categories.filter((c) => activeKeys.includes(c.key)), [categories, activeKeys]);
  const cat = steps[step];
  const startWith = (keys) => {
    setActiveKeys(keys);
    setStep(0);
    setView("build");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const addCategory = (key) => {
    const keys = categories.map((c) => c.key).filter((k) => activeKeys.includes(k) || k === key);
    setActiveKeys(keys);
    setStep(keys.indexOf(key));
    setView("build");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const hull = hullOf(boatLine?.product);
  const stepProducts = useMemo(() => {
    const all = productsByCat[cat?.key] || [];
    if (!cat || cat.key === "boat" || !hull || showAllFits[cat.key] || !hasFitRules(all)) return { shown: all, hidden: 0 };
    const shown = all.filter((p) => productFitsHull(p, hull) || selections[p.id]);
    return { shown, hidden: all.length - shown.length };
  }, [productsByCat, cat, hull, showAllFits, selections]);

  const toggle = (product, selectType) => {
    setSelections((prev) => {
      const next = { ...prev };
      if (next[product.id]) {
        delete next[product.id];
        return next;
      }
      if (selectType === "single") {
        for (const id of Object.keys(next)) {
          if (next[id].product.category === product.category) delete next[id];
        }
      }
      const boat = Object.values(next).find((l) => l.product.category === "boat")?.product;
      const h = hullOf(boat);
      const quantity = product.unit === "sq ft" && h?.length && h?.width ? Math.round((h.length * h.width) / 12) : 1;
      next[product.id] = { product, quantity, selectType, ...defaultSelection(product, optionSets) };
      return next;
    });
  };

  const setQty = (product, quantity) => {
    setSelections((prev) => ({ ...prev, [product.id]: { ...prev[product.id], quantity } }));
  };

  const setVariant = (product, group, label) => {
    const g = effectiveOptions(product, optionSets).variants.find((x) => x.name === group);
    setSelections((prev) => {
      const cur = prev[product.id].variants || {};
      const value = g?.multi ? toggleMulti(cur[group], label) : label;
      return { ...prev, [product.id]: { ...prev[product.id], variants: { ...cur, [group]: value } } };
    });
  };

  const setVariantQty = (product, group, label, qty) => {
    setSelections((prev) => {
      const cur = prev[product.id].variants || {};
      return { ...prev, [product.id]: { ...prev[product.id], variants: { ...cur, [group]: setMultiQty(cur[group], label, qty) } } };
    });
  };

  const toggleAddon = (product, label) => {
    setSelections((prev) => {
      const cur = prev[product.id];
      const addons = cur.addons.includes(label) ? cur.addons.filter((a) => a !== label) : [...cur.addons, label];
      return { ...prev, [product.id]: { ...cur, addons } };
    });
  };

  const removeItem = (product) => {
    setSelections((prev) => {
      const next = { ...prev };
      delete next[product.id];
      return next;
    });
  };

  const goNext = () => {
    if (step < steps.length - 1) setStep(step + 1);
    else setView("review");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const goBack = () => {
    if (view === "review") {
      setView("build");
      setStep(steps.length - 1);
    } else if (step > 0) setStep(step - 1);
    else setView("pick");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const goHero = () => {
    setView("hero");
    Object.keys(sessionStorage).filter((k) => k.startsWith("fdm_step_")).forEach((k) => sessionStorage.removeItem(k));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const itemsPayload = () =>
    lines.map((l) => ({
      product_id: l.product.id,
      category: l.product.category,
      name: l.product.name,
      price: unitPrice(l.product, l.variants, l.addons, optionSets),
      quantity: l.quantity,
      unit: l.product.unit || "",
      image_url: l.product.image_url,
      variants: l.variants,
      addons: l.addons,
    }));

  const shareBuild = async () => {
    setSharing(true);
    try {
      const res = await api.post("/builds", { items: itemsPayload() });
      const url = `${window.location.origin}/?build=${res.data.code}`;
      setShareUrl(url);
      setCopied(false);
      try {
        await navigator.clipboard.writeText(url);
        setCopied(true);
      } catch (_) {
        /* clipboard blocked — user can copy from the dialog */
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save your build.");
    } finally {
      setSharing(false);
    }
  };

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied.");
    } catch (_) {
      toast.error("Copy blocked — select the link and copy manually.");
    }
  };

  const submitQuote = async () => {
    if (!form.name.trim()) return toast.error("Please enter your name.");
    if (!form.email.trim()) return toast.error("Please enter your email.");
    if (lines.length === 0) return toast.error("Your build is empty.");
    setSubmitting(true);
    try {
      const res = await api.post("/quotes", { ...form, items: itemsPayload() });
      setResult(res.data);
      setView("done");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit quote. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const summaryProps = {
    lines,
    total,
    shippingTotal,
    onRemove: removeItem,
    onQty: setQty,
    catLabels,
    optionSets,
    onShare: shareBuild,
    sharing,
  };

  const shareDialog = (
    <Dialog open={!!shareUrl} onOpenChange={(o) => !o && setShareUrl("")}>
      <DialogContent data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Your build is saved</DialogTitle>
          <DialogDescription>
            Anyone with this link can open your exact build — send it to a buddy or come back to it later.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2">
          <Input data-testid="share-url-input" readOnly value={shareUrl} onFocus={(e) => e.target.select()} />
          <Button data-testid="share-copy-button" onClick={copyShare} className="bg-[#1B66C9] font-bold hover:bg-[#144E9C]">
            {copied ? <Check size={16} /> : <Copy size={16} />}
          </Button>
        </div>
        {copied && <p className="text-xs font-semibold text-[#16A34A]">Copied to clipboard.</p>}
      </DialogContent>
    </Dialog>
  );

  /* ---------------- HERO ---------------- */
  if (view === "hero") {
    return (
      <div className="min-h-screen">
        <Header showNav={false} />
        <section className="relative overflow-hidden">
          <div className="absolute inset-0">
            <img src={HERO} alt="" className="h-full w-full object-cover object-[70%_center]" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/60 to-transparent" />
          </div>
          <div className="relative mx-auto flex min-h-[calc(100vh-64px)] max-w-7xl flex-col justify-center px-5 py-20 sm:px-8">
            <span className="mb-5 w-fit rounded-full border border-white/30 bg-white/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.25em] text-white backdrop-blur">
              Instant Quote Builder
            </span>
            <h1 className="max-w-3xl font-display text-4xl font-black leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
              Get a quote on a full rig, a new motor, or just the parts you need.
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-white/80 sm:text-lg">
              No minimums — quote one item or the whole package. Pick what you want, see live pricing, and
              get a detailed quote emailed to you in minutes. No pressure, no phone tag.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Button
                data-testid="start-building-button"
                onClick={() => setView("pick")}
                className="h-14 rounded-md bg-[#1B66C9] px-8 text-base font-bold hover:bg-[#144E9C]"
              >
                Start My Quote <ArrowRight className="ml-2" size={18} />
              </Button>
              <a
                href="tel:3867477652"
                data-testid="hero-call-link"
                className="flex items-center gap-2 text-sm font-bold text-white/90 hover:text-white"
              >
                <Phone size={16} /> 386-747-7652
              </a>
            </div>
          </div>
        </section>
      </div>
    );
  }

  /* ---------------- PICK ---------------- */
  if (view === "pick") {
    return (
      <div className="min-h-screen">
        <Header onHome={goHero} onBack={goHero} />
        <QuotePicker categories={categories} onContinue={startWith} />
      </div>
    );
  }

  /* ---------------- DONE ---------------- */
  if (view === "done" && result) {
    return (
      <div className="min-h-screen">
        <Header onHome={goHero} onBack={() => setView("review")} />
        <div className="mx-auto max-w-2xl px-5 py-20 text-center sm:px-8">
          <CheckCircle2 className="mx-auto text-[#16A34A]" size={64} />
          <h1 className="mt-6 font-display text-3xl font-black tracking-tight sm:text-4xl">
            Quote sent!
          </h1>
          <p className="mt-3 text-muted-foreground">
            We emailed your quote <span className="font-bold text-foreground">{result.quote_number}</span> to{" "}
            <span className="font-bold text-foreground">{result.email}</span>. Our team will follow up
            shortly. Questions? Call or text 386-747-7652.
          </p>
          <div className="mx-auto mt-8 max-w-md rounded-lg border border-border bg-card p-6 text-left">
            <div className="flex items-center justify-between border-b border-border pb-3">
              <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                {result.quote_number}
              </span>
              <span className="font-display text-2xl font-black text-[#1B66C9]">
                {money(result.total)}
              </span>
            </div>
            <ul className="mt-3 space-y-2">
              {result.items.map((it) => (
                <li key={it.product_id} className="flex justify-between gap-3 text-sm">
                  <span>
                    {it.name} {it.quantity > 1 && `× ${it.quantity}`}
                    {variantText(it.variants, it.addons) && (
                      <span className="block text-xs text-muted-foreground">{variantText(it.variants, it.addons)}</span>
                    )}
                  </span>
                  <span className="font-semibold">{money(it.price * it.quantity)}</span>
                </li>
              ))}
              {result.shipping_total > 0 && (
                <li className="flex justify-between gap-3 text-sm" data-testid="result-shipping">
                  <span>
                    Shipping <span className="block text-xs text-muted-foreground">Flat-rate freight to Fast Duck Motors</span>
                  </span>
                  <span className="font-semibold">{money(result.shipping_total)}</span>
                </li>
              )}
            </ul>
          </div>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Button
              asChild
              data-testid="download-pdf-button"
              className="h-12 rounded-md bg-[#1B66C9] px-6 font-bold hover:bg-[#144E9C]"
            >
              <a href={quotePdfUrl(result.id)} target="_blank" rel="noreferrer">
                <FileDown className="mr-2" size={18} /> Download PDF
              </a>
            </Button>
            <Button
              data-testid="build-another-button"
              onClick={() => {
                setSelections({});
                setForm({ name: "", phone: "", email: "", comments: "" });
                setResult(null);
                setActiveKeys([]);
                window.history.replaceState(null, "", window.location.pathname);
                setView("hero");
              }}
              className="h-12 rounded-md bg-[#1E231F] px-8 font-bold text-white hover:bg-black dark:bg-white dark:text-black dark:hover:bg-white/90"
            >
              Build Another
            </Button>
          </div>
        </div>
      </div>
    );
  }

  /* ---------------- BUILD + REVIEW ---------------- */
  return (
    <div className="min-h-screen pb-28 lg:pb-0">
      <Header onHome={goHero} onBack={goBack} />
      {shareDialog}

      {/* Stepper */}
      <div className="border-b border-border bg-card/50">
        <div className="mx-auto max-w-7xl px-5 py-4 sm:px-8">
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
            {steps.map((c, i) => {
              const active = view === "build" && i === step;
              const done = (view === "build" && i < step) || view === "review";
              return (
                <button
                  key={c.key}
                  data-testid={`stepper-${c.key}`}
                  onClick={() => {
                    setView("build");
                    setStep(i);
                  }}
                  className={`flex-shrink-0 rounded-full px-3.5 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                    active
                      ? "bg-[#1B66C9] text-white"
                      : done
                      ? "bg-secondary text-foreground"
                      : "text-muted-foreground hover:bg-secondary"
                  }`}
                >
                  {i + 1}. {c.label}
                </button>
              );
            })}
            <button
              data-testid="stepper-review"
              onClick={() => setView("review")}
              className={`flex-shrink-0 rounded-full px-3.5 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                view === "review" ? "bg-[#1B66C9] text-white" : "text-muted-foreground hover:bg-secondary"
              }`}
            >
              Review
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-5 py-8 sm:px-8">
        <div className="grid grid-cols-12 gap-8">
          {/* Main */}
          <div className="col-span-12 lg:col-span-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={view + step}
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.25 }}
              >
                {view === "build" && cat && (
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#1B66C9]">
                      Step {step + 1} of {steps.length}
                    </p>
                    <h2 className="mt-1 font-display text-2xl font-black tracking-tight sm:text-3xl">
                      {cat.label}
                    </h2>
                    <p className="mt-1 text-muted-foreground">
                      {cat.tagline}
                      {cat.select === "multi" && " · select any that apply"}
                      {cat.select === "single" && !cat.required && " · optional"}
                    </p>

                    {cat.key === "boat" || cat.key === "motor" ? (
                      <div className="mt-6">
                        <BoatStep
                          key={cat.key}
                          noun={cat.key === "boat" ? "hull" : "motor"}
                          brands={cat.key === "boat" ? brands : motorBrands}
                          boats={productsByCat[cat.key] || []}
                          selection={cat.key === "boat" ? boatLine : motorLine}
                          optionSets={optionSets}
                          onSelect={(p) => toggle(p, "single")}
                          onBrandChange={cat.key === "boat" ? setBoatBrand : undefined}
                          onVariant={setVariant}
                          onVariantQty={setVariantQty}
                          onAddon={toggleAddon}
                        />
                      </div>
                    ) : (
                    <>
                    {hull && hasFitRules(productsByCat[cat.key] || []) && (
                      <div
                        data-testid="step-fit-banner"
                        className="mt-6 flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-secondary/50 px-4 py-2.5 text-xs"
                      >
                        <span className="flex items-center gap-1.5 font-semibold">
                          <Ruler size={13} className="text-[#1B66C9]" />
                          {showAllFits[cat.key]
                            ? `Showing every ${cat.label.toLowerCase()}, including ones not sized for your hull`
                            : `Showing ${cat.label.toLowerCase()}s sized for your ${hullLabel(hull)} hull${stepProducts.hidden ? ` · ${stepProducts.hidden} hidden` : ""}`}
                        </span>
                        <button
                          data-testid="step-fit-toggle"
                          onClick={() => setShowAllFits((v) => ({ ...v, [cat.key]: !v[cat.key] }))}
                          className="font-bold uppercase tracking-wider text-[#1B66C9] hover:underline"
                        >
                          {showAllFits[cat.key] ? "Show only what fits" : "Show all"}
                        </button>
                      </div>
                    )}
                    <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
                      {stepProducts.shown.map((p, idx) => (
                        <Fragment key={p.id}>
                        {p.brand && stepProducts.shown.findIndex((q) => q.brand === p.brand) === idx && (
                          <h3 data-testid={`brand-heading-${slug(p.brand)}`} className="col-span-full mt-2 font-display text-sm font-black uppercase tracking-[0.2em] text-muted-foreground first:mt-0">
                            {p.brand}
                          </h3>
                        )}
                        <OptionCard
                          product={p}
                          selected={!!selections[p.id]}
                          selectType={cat.select}
                          quantity={selections[p.id]?.quantity || 1}
                          variants={selections[p.id]?.variants || {}}
                          optionSets={optionSets}
                          onToggle={() => toggle(p, cat.select)}
                          onQty={(q) => setQty(p, q)}
                          onVariant={(g, l) => setVariant(p, g, l)}
                          testId={`option-${cat.key}-${idx}`}
                        />
                        </Fragment>
                      ))}
                      {stepProducts.shown.length === 0 && (
                        <p className="col-span-full py-10 text-center text-muted-foreground" data-testid="step-empty">
                          {stepProducts.hidden
                            ? `None of our ${cat.label.toLowerCase()}s are sized for a ${hullLabel(hull)} hull — tap "Show all" or mention it in your comments and we'll find one.`
                            : "No options in this category yet."}
                        </p>
                      )}
                    </div>
                    </>
                    )}
                  </div>
                )}

                {view === "review" && (
                  <div>
                    <h2 className="font-display text-2xl font-black tracking-tight sm:text-3xl">
                      Review & get your quote
                    </h2>
                    <p className="mt-1 text-muted-foreground">
                      Enter your details and we'll email your quote (with a printable PDF) right away.
                    </p>

                    {categories.some((c) => !activeKeys.includes(c.key)) && (
                      <div className="mt-5 rounded-lg border border-dashed border-border bg-secondary/40 p-4" data-testid="add-more-panel">
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Want anything else quoted?</p>
                        <div className="mt-2.5 flex flex-wrap gap-2">
                          {categories
                            .filter((c) => !activeKeys.includes(c.key))
                            .map((c) => (
                              <button
                                key={c.key}
                                data-testid={`add-more-${c.key}`}
                                onClick={() => addCategory(c.key)}
                                className="flex items-center gap-1.5 rounded-full border border-border bg-card px-3.5 py-1.5 text-xs font-bold transition-colors hover:border-[#1B66C9] hover:text-[#1B66C9]"
                              >
                                <Plus size={13} /> Add {c.label.toLowerCase()}
                              </button>
                            ))}
                        </div>
                      </div>
                    )}

                    <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
                      <div>
                        <Label htmlFor="name">Name *</Label>
                        <Input
                          id="name"
                          data-testid="quote-name-input"
                          value={form.name}
                          onChange={(e) => setForm({ ...form, name: e.target.value })}
                          placeholder="John Waterman"
                          className="mt-1.5"
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone">Phone</Label>
                        <Input
                          id="phone"
                          data-testid="quote-phone-input"
                          value={form.phone}
                          onChange={(e) => setForm({ ...form, phone: e.target.value })}
                          placeholder="(386) 555-0199"
                          className="mt-1.5"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          data-testid="quote-email-input"
                          value={form.email}
                          onChange={(e) => setForm({ ...form, email: e.target.value })}
                          placeholder="you@email.com"
                          className="mt-1.5"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <Label htmlFor="comments">Comments</Label>
                        <Textarea
                          id="comments"
                          data-testid="quote-comments-input"
                          value={form.comments}
                          onChange={(e) => setForm({ ...form, comments: e.target.value })}
                          placeholder="Anything you'd like to add that wasn't in the options, or questions you have."
                          className="mt-1.5 min-h-[110px]"
                        />
                      </div>
                    </div>

                    <Button
                      data-testid="quote-submit-button"
                      onClick={submitQuote}
                      disabled={submitting}
                      className="mt-6 h-13 w-full rounded-md bg-[#1B66C9] py-6 text-base font-bold hover:bg-[#144E9C] sm:w-auto sm:px-10"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="mr-2 animate-spin" size={18} /> Sending…
                        </>
                      ) : (
                        <>Get My Quote — {money(total)}</>
                      )}
                    </Button>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
            {view === "build" && cat?.key === "boat" && <StandardFeatures brand={boatBrand} className="mt-6 lg:hidden" />}

            {/* Nav buttons */}
            <div className="mt-8 flex items-center justify-between border-t border-border pt-6">
              <Button
                variant="ghost"
                data-testid="nav-back-button"
                onClick={goBack}
                className="font-bold"
              >
                <ArrowLeft className="mr-2" size={18} /> Back
              </Button>
              {view === "build" && (
                <Button
                  data-testid="nav-next-button"
                  onClick={goNext}
                  className="h-12 rounded-md bg-[#1E231F] px-7 font-bold text-white hover:bg-black dark:bg-white dark:text-black dark:hover:bg-white/90"
                >
                  {step < steps.length - 1 ? `Next: ${steps[step + 1].label}` : "Review & Quote"}
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              )}
            </div>
          </div>

          {/* Desktop summary */}
          <aside className="col-span-12 hidden lg:col-span-4 lg:block">
            <div className="sticky top-24 space-y-4">
              <div className="rounded-lg border border-border bg-card p-5">
                <SummaryPanel {...summaryProps} />
              </div>
              {view === "build" && steps[step]?.key === "boat" && <StandardFeatures brand={boatBrand} />}
            </div>
          </aside>
        </div>
      </div>

      {/* Mobile sticky bar */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/90 backdrop-blur-xl lg:hidden">
        <div className="flex items-center justify-between gap-3 px-5 py-3">
          <Dialog>
            <DialogTrigger asChild>
              <button data-testid="mobile-summary-trigger" className="flex flex-col items-start">
                <span className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                  <ClipboardList size={13} /> {lines.length} item{lines.length !== 1 ? "s" : ""}
                </span>
                <span className="font-display text-xl font-black text-[#1B66C9]">{money(total)}</span>
              </button>
            </DialogTrigger>
            <DialogContent className="max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-display">Your Build</DialogTitle>
                <DialogDescription className="sr-only">Items in your current build</DialogDescription>
              </DialogHeader>
              <SummaryPanel {...summaryProps} />
            </DialogContent>
          </Dialog>

          {view === "build" ? (
            <Button
              data-testid="mobile-next-button"
              onClick={goNext}
              className="h-12 flex-1 rounded-md bg-[#1B66C9] font-bold hover:bg-[#144E9C]"
            >
              {step < steps.length - 1 ? "Next" : "Review"} <ArrowRight className="ml-1.5" size={17} />
            </Button>
          ) : (
            <Button
              data-testid="mobile-submit-button"
              onClick={submitQuote}
              disabled={submitting}
              className="h-12 flex-1 rounded-md bg-[#1B66C9] font-bold hover:bg-[#144E9C]"
            >
              {submitting ? <Loader2 className="animate-spin" size={18} /> : "Get My Quote"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
